import { getUrl } from "./url";

// Hostnames where Powered By SHY tenant resolution should not run
export const SHY_MAIN_SITE_HOSTNAMES = [
    "localhost",
    "witty-bay-072957d03.1.azurestaticapps.net",
    "purple-mud-0efcaf403.1.azurestaticapps.net/",
    "shyaviation.com",
    "www.shyaviation.com",
    "booking.shyaviation.com",
    "shy-static-dev.azurewebsites.net",
    "shy-wa-dev-fe-cmhucxawcfbvfhdk.uksouth-01.azurewebsites.net",
    "shy-wa-dev-cms-e6c5bxhgbgfnecam.uksouth-01.azurewebsites.net",
    "front.shyaviation.com",
];

/**
 * @param {string} [hostname]
 * @returns {boolean}
 */
export const isShyMainSite = (hostname = window.location.hostname) => {
    const queryParams = new URLSearchParams(window.location.search);
    return SHY_MAIN_SITE_HOSTNAMES.indexOf(hostname) > -1 && queryParams.get("tenantID") === null;
};

/**
 * @param {Object} poweredByCtx - Powered-by context (tenantID)
 * @returns {boolean}
 */
export const isPelorus = (poweredByCtx) => {
    return poweredByCtx?.tenantID === "PLRS";
};

/**
 * @param {Object} poweredByCtx - Powered-by context (tenantID)
 * @returns {boolean}
 */
export const isManchesterUnited = (poweredByCtx) => {
    return poweredByCtx?.tenantID?.toLowerCase() === "mutd";
};

/**
 * @param {Object} poweredByCtx - Powered-by context (tenant with social keys)
 * @returns {string[]}
 */
const getSchemaSocialLinks = (poweredByCtx) => {
    let options = ["instagram", "facebook", "twitter", "tiktok", "linkedin"];

    const links = options.map((option) => {
        return poweredByCtx?.tenant[option];
    });

    return links.filter((link) => {
        return link !== undefined;
    });
};

/**
 * @param {Object} poweredByCtx - Powered-by context (isPoweredBy, tenant, tenantUrl, etc.)
 * @returns {Object} JSON-LD Organization schema
 */
export const getSchema = (poweredByCtx) => {
    const context = "https://schema.org";
    const type = "Organization";
    const id = getUrl(poweredByCtx, "/#organisation");
    const url = getUrl(poweredByCtx, "/");
    const description =
        "We are a global aviation charter company, with access to every available aircraft on the market.";

    if (poweredByCtx.isPoweredBy) {
        return {
            "@context": context,
            "@type": type,
            "@id": id,
            name: poweredByCtx?.tenant?.tenantName,
            url: url,
            logo: poweredByCtx?.tenant?.appleTouchIcon,
            description: description,
            sameAs: getSchemaSocialLinks(poweredByCtx),
        };
    } else {
        return {
            "@context": context,
            "@type": type,
            "@id": id,
            name: "Shy Aviation",
            url: url,
            address: {
                "@type": "PostalAddress",
                addressLocality: "London",
                addressCountry: "United Kingdom",
                postalCode: "WC1V 7DN",
                streetAddress: "233 High Holborn",
            },
            logo: "/icons/shy-logo.png",
            description: description,
            sameAs: [
                "https://www.instagram.com/shyaviation_official",
                "https://www.facebook.com/shyaviation/",
                "https://twitter.com/shyaviation",
                "https://www.tiktok.com/@shyaviation",
                "https://www.linkedin.com/company/shy-aviation",
            ],
        };
    }
};

/**
 * Page-specific structured data.
 * Returns a schema object (BlogPosting / Service / WebSite) or null when the
 * page has no specific type beyond the site-wide Organization schema.
 *
 * References the Organization via @id so search/AI crawlers link the two.
 */
export const getPageSchema = ({ poweredByCtx, type, title, description, image, date, pathname, isHomepage }) => {
    const context = "https://schema.org";
    const orgId = getUrl(poweredByCtx, "/#organisation");
    const siteName = poweredByCtx.isPoweredBy ? poweredByCtx?.tenant?.tenantName : "Shy Aviation";
    const url = getUrl(poweredByCtx, isHomepage ? "/" : pathname || "/");

    // Blog article
    if (type === "article") {
        const schema = {
            "@context": context,
            "@type": "BlogPosting",
            "@id": `${url}#article`,
            mainEntityOfPage: url,
            url: url,
            headline: title,
            description: description,
            author: { "@id": orgId },
            publisher: { "@id": orgId },
        };
        if (image) schema.image = image;
        if (date) {
            schema.datePublished = date;
            schema.dateModified = date;
        }
        return schema;
    }

    // "/fly/..." destination / charter landing pages -> Service
    if (pathname && pathname.startsWith("/fly/")) {
        const schema = {
            "@context": context,
            "@type": "Service",
            "@id": `${url}#service`,
            name: title,
            description: description,
            serviceType: "Private jet charter",
            provider: { "@id": orgId },
            url: url,
        };
        if (image) schema.image = image;
        return schema;
    }

    // Homepage -> WebSite
    if (isHomepage) {
        const homeUrl = getUrl(poweredByCtx, "/");
        return {
            "@context": context,
            "@type": "WebSite",
            "@id": `${homeUrl}#website`,
            url: homeUrl,
            name: siteName,
            publisher: { "@id": orgId },
        };
    }

    return null;
};
